import instance from "../axiosConfig";
import { Software, UseCase } from "../global-services/models";
import {
    SettingsDiscipline,
    SettingsFooterLink,
    SettingsFootnote,
    SettingsLanguage,
    SettingsNotification,
    SettingsPhase,
} from "./models";

export class SettingsService {
    private regionCode: string;

    constructor(regionCode: string) {
        this.regionCode = regionCode;
    }

    getLanguages = async (): Promise<SettingsLanguage[]> => {
        const response = await instance.get<SettingsLanguage[]>(`api/Admin/${this.regionCode}/Settings/Languages`);

        return response.data;
    };

    createLanguage = async (body: SettingsLanguage): Promise<SettingsLanguage> => {
        const response = await instance.post<SettingsLanguage>(`api/Admin/${this.regionCode}/Settings/Languages`, body);

        return response.data;
    };

    updateLanguage = async (slug: string, body: SettingsLanguage): Promise<SettingsLanguage> => {
        const response = await instance.post<SettingsLanguage>(
            `api/Admin/${this.regionCode}/Settings/Languages/${slug}`,
            body,
            {
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        return response.data;
    };

    removeLanguage = async (slug: string): Promise<boolean> => {
        try {
            const response = await instance.delete<SettingsLanguage>(
                `api/Admin/${this.regionCode}/Settings/Languages/${slug}`
            );

            return response.status >= 200 && response.status < 300;
        } catch {
            return false;
        }
    };

    getDisciplines = async (): Promise<SettingsDiscipline[]> => {
        const response = await instance.get<SettingsDiscipline[]>(`api/Admin/${this.regionCode}/Settings/Disciplines`);

        return response.data;
    };

    createDiscipline = async (body: SettingsDiscipline): Promise<SettingsDiscipline> => {
        const response = await instance.post<SettingsDiscipline>(
            `api/Admin/${this.regionCode}/Settings/Disciplines`,
            body
        );

        return response.data;
    };

    updateDiscipline = async (id: string, body: SettingsDiscipline): Promise<SettingsDiscipline> => {
        const response = await instance.post<SettingsDiscipline>(
            `api/Admin/${this.regionCode}/Settings/Disciplines/${id}`,
            body,
            {
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        return response.data;
    };

    removeDiscipline = async (id: string): Promise<boolean> => {
        try {
            const response = await instance.delete<SettingsDiscipline>(
                `api/Admin/${this.regionCode}/Settings/Disciplines/${id}`
            );

            return response.status >= 200 && response.status < 300;
        } catch {
            return false;
        }
    };

    getPhases = async (): Promise<SettingsPhase[]> => {
        const response = await instance.get<SettingsPhase[]>(`api/Admin/${this.regionCode}/Settings/Phases`);

        return response.data;
    };

    createPhase = async (body: SettingsPhase): Promise<SettingsPhase> => {
        const response = await instance.post<SettingsPhase>(`api/Admin/${this.regionCode}/Settings/Phases`, body);

        return response.data;
    };

    updatePhase = async (id: string, body: SettingsPhase): Promise<SettingsPhase> => {
        const response = await instance.post<SettingsPhase>(
            `api/Admin/${this.regionCode}/Settings/Phases/${id}`,
            body,
            {
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        return response.data;
    };

    removePhase = async (id: string): Promise<boolean> => {
        try {
            const response = await instance.delete<SettingsPhase>(`api/Admin/${this.regionCode}/Settings/Phases/${id}`);

            return response.status >= 200 && response.status < 300;
        } catch {
            return false;
        }
    };

    getBimqParameters = async (): Promise<Software[]> => {
        const response = await instance.get<Software[]>(`api/Admin/${this.regionCode}/Settings/Software`);

        return response.data;
    };

    updateBimqParameters = async (id: string, body: Software): Promise<Software> => {
        const response = await instance.post<Software>(`api/Admin/${this.regionCode}/Settings/Software/${id}`, body, {
            headers: {
                "Content-Type": "application/json",
            },
        });

        return response.data;
    };

    getUseCases = async (languageCode: string): Promise<UseCase[]> => {
        const response = await instance.get<UseCase[]>(
            `api/regions/${this.regionCode}/languages/${languageCode}/UseCases?version=wip`
        );

        return response.data;
    };

    updateUseCaseLink = async (id: string, languageCode: string, link: string): Promise<UseCase> => {
        const response = await instance.patch<UseCase>(
            `api/regions/${this.regionCode}/languages/${languageCode}/UseCases/${id}`,
            { internalLink: link },
            {
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        return response.data;
    };

    getFootnotes = async (): Promise<string[]> => {
        const response = await instance.get<string[]>(`api/Admin/${this.regionCode}/Footnotes`);

        return response.data;
    };

    getFootnote = async (id: string): Promise<SettingsFootnote> => {
        const response = await instance.get<SettingsFootnote>(`api/Admin/${this.regionCode}/Footnotes/${id}`);

        return response.data;
    };

    updateFootnote = async (id: string, body: SettingsFootnote): Promise<SettingsFootnote> => {
        const response = await instance.post<SettingsFootnote>(`api/Admin/${this.regionCode}/Footnotes/${id}`, body, {
            headers: {
                "Content-Type": "application/json",
            },
        });

        return response.data;
    };

    getNotification = async (): Promise<SettingsNotification[]> => {
        const response = await instance.get<SettingsNotification[]>(`api/Admin/${this.regionCode}/Notification`);

        return response.data;
    };

    updateNotification = async (body: SettingsNotification): Promise<SettingsNotification> => {
        const response = await instance.post<SettingsNotification>(`api/Admin/${this.regionCode}/Notification`, body, {
            headers: {
                "Content-Type": "application/json",
            },
        });

        return response.data;
    };

    removeNotification = async (id: string): Promise<boolean> => {
        try {
            const response = await instance.delete<SettingsNotification>(
                `api/Admin/${this.regionCode}/Notification/${id}`
            );

            return response.status >= 200 && response.status < 300;
        } catch {
            return false;
        }
    };

    getFooterLinks = async (): Promise<SettingsFooterLink[]> => {
        const response = await instance.get<SettingsFooterLink[]>(`api/Admin/${this.regionCode}/FooterLinks`);

        return response.data;
    };

    createFooterLink = async (body: SettingsFooterLink): Promise<SettingsFooterLink> => {
        const response = await instance.post<SettingsFooterLink>(`api/Admin/${this.regionCode}/FooterLinks`, body);

        return response.data;
    };

    updateFooterLink = async (id: string, body: SettingsFooterLink): Promise<SettingsFooterLink> => {
        const response = await instance.put<SettingsFooterLink>(
            `api/Admin/${this.regionCode}/FooterLinks/${id}`,
            body,
            {
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        return response.data;
    };

    removeFooterLink = async (id: string): Promise<boolean> => {
        try {
            const response = await instance.delete<SettingsFooterLink>(
                `api/Admin/${this.regionCode}/FooterLinks/${id}`
            );

            return response.status >= 200 && response.status < 300;
        } catch {
            return false;
        }
    };
}
